import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { dayOffSuffix } from "../../shared utils/personDetail";

// Create an Arrivers/Departers holding group from a candidate card. The name and
// DAY slot are fixed; the user just picks the supervising staff member (the group
// leader). Members are passed in from the card (today's arrivers or departers),
// so membership is arrivers/departers-only by construction.
export default function CreateHoldingGroupDialog({
  open,
  onClose,
  kind, // "ARRIVERS" | "DEPARTERS"
  members = [],
  leaders = [],
  campusLabel,
  slot,
  onConfirm,
}) {
  const [leaderStaffId, setLeaderStaffId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLeaderStaffId("");
      setSaving(false);
    }
  }, [open]);

  const label = kind === "DEPARTERS" ? "Departers" : "Arrivers";

  const counts = useMemo(() => {
    const adults = (members || []).filter(
      (m) => String(m?.personType).toUpperCase() === "ADULT"
    ).length;
    return { adults, students: (members || []).length - adults, total: (members || []).length };
  }, [members]);

  const handleConfirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onConfirm?.({ leaderStaffId: leaderStaffId || null });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Create {label} group</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            {campusLabel ? `${campusLabel} · ` : ""}
            {slot ? `${slot} · ` : ""}
            {counts.total} member{counts.total === 1 ? "" : "s"} ({counts.adults} adults •{" "}
            {counts.students} students)
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel id="holding-leader">Supervising staff</InputLabel>
            <Select
              labelId="holding-leader"
              label="Supervising staff"
              value={leaderStaffId}
              onChange={(e) => setLeaderStaffId(e.target.value)}
            >
              <MenuItem value="">
                <em>None for now</em>
              </MenuItem>
              {(leaders || []).map((leader) => (
                <MenuItem key={leader.staffId} value={String(leader.staffId)}>
                  {leader.name}
                  {dayOffSuffix(leader)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving ? "Creating…" : `Create with ${counts.total}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
